export const i18n: Record<string, any> = {
  en: {
    nav: { generate: "Generate Poster", map: "Map", advanced: "Advanced", gallery: "Gallery" },
    form: {
      title: "Customize Your Poster",
      popularCities: "Popular Cities",
      city: "City",
      cityPlaceholder: "Enter city name",
      country: "Country",
      countryPlaceholder: "Enter country name",
      theme: "Style Theme",
      zoom: "Zoom/Distance",
      generate: "Generate New View",
      printOptions: "Print Options",
      vertical: "Vertical",
      square: "Square",
      coordinates: "Coordinates",
      editOnMap: "Edit on map",
      buyPhysical: "Buy Physical",
      stop: "Stop Generating",
    },
    preview: { placeholder: "Enter a city and generate your poster" },
    map: {
      title: "Select Location",
      instructions:
        "Click on the map to set the center point for your poster. Use zoom controls or scroll to adjust the view.",
      center: "Center Point",
      radius: "Poster Radius",
      search: "Search Location",
      searchPlaceholder: "Search for a place...",
      apply: "Apply & Generate",
    },
    download: {
      ready: "Ready to print?",
      resolution: "Ultra High Resolution (300 DPI)",
      png: "PNG",
      svg: "SVG",
      pdf: "PDF",
      share: "Share",
      shareCopied: "Link copied to clipboard!",
      shareError: "Failed to copy link",
    },
    carousel: {
      title: "Choose Theme",
      loading: "Generating...",
      tip: "Tip: Use the carousel below to quickly switch between different visual themes.",
    },
    gallery: {
      title: "My Gallery",
      count: "You have {count} saved map poster",
      countPlural: "You have {count} saved map posters",
      empty: "No posters yet",
      emptyDesc: "Generate your first poster to get started!",
      createFirst: "Create First Poster",
      allPosters: "All Posters",
      newPoster: "New Poster",
      newPosterDesc: "Create a new design",
    },
    modal: {
      galleryView: "Gallery View",
      downloadPoster: "Download Poster",
      artwork: "Artwork {current} of {total}",
      previous: "Previous",
      next: "Next",
    },
    footer: {
      copyright: "© 2024 Map Poster Generator. All rights reserved.",
      terms: "Terms of Service",
      privacy: "Privacy Policy",
      help: "Help Center",
    },
    privacy: {
      title: "Privacy & Experience",
      text:
        "We use local storage to remember your shipping details and settings for a faster experience. Your data never leaves your device unless you place an order.",
      accept: "Got it",
    },
    advanced: {
      title: "Advanced Settings",
      customText: "Custom Text",
      customTextDesc:
        "Override the default city/country text on your poster. Leave empty to use detected values.",
      cityText: "City Text",
      cityPlaceholder: "e.g. COPENHAGEN",
      countryText: "Country Text",
      countryPlaceholder: "e.g. DENMARK",
      coordinates: "Coordinates",
      coordinatesDesc:
        "Override the coordinates displayed on the poster. Leave empty to use detected values.",
      latitude: "Latitude",
      longitude: "Longitude",
      typography: "Typography",
      typographyDesc: "Choose the font style for text on your poster.",
      fontFamily: "Font Family",
      layers: "Map Layers",
      layersDesc: "Toggle heavy map layers to prevent data fetching errors.",
      showBuildings: "Show Buildings",
      showRailways: "Show Railways",
      reset: "Reset to Defaults",
    },
    loading: {
      finding: "Finding location...",
      fetching: "Fetching map data...",
      rendering: "Rendering poster...",
      saving: "Saving to gallery...",
      applying: "Applying new theme...",
      generating: "Generating...",
    },
    errors: {
      cityCountry: "Please enter both city and country",
      geocode: "Failed to geocode location",
      generate: "Failed to generate poster",
    },
    pod: {
      title: "Order Physical Product",
      demoMode: "Demo Mode - no real orders",
      testMode: "Stripe test mode",
      paymentNotConfigured: "Payment not configured",
      shippingCountry: "Shipping country",
      comingSoon: "Coming Soon",
      comingSoonDesc: "Physical prints are on their way. Check back soon!",
      wallArt: "Wall Art",
      apparel: "Apparel",
      accessories: "Accessories",
      findingProducts: "Finding available products for {country}...",
      notAvailableIn: "Not available in {country}",
      unavailable: "unavailable",
      fromPrice: "from {price}",
      backToProducts: "Back to products",
      size: "Size",
      quantity: "Quantity",
      price: "Price",
      backToConfig: "Back to configuration",
      backToShipping: "Back to shipping",
      shippingAddress: "Shipping Address",
      firstName: "First Name",
      lastName: "Last Name",
      email: "Email",
      address1: "Address Line 1",
      address2: "Address Line 2 (optional)",
      city: "City",
      postcode: "Postcode",
      state: "State / Province",
      termsTitle: "Terms of Sale & Privacy",
      termsText:
        "I understand this is a custom-made product. By placing this order, I explicitly waive my 14-day right of withdrawal. I agree that my address and design will be shared with Gelato for fulfillment. Colors may vary slightly from the screen preview.",
      orderSummary: "Order Summary",
      shippingEstimate: "Shipping calculated by provider. Estimated delivery: 5-10 business days.",
      orderPlaced: "Order Placed!",
      orderNumber: "Order #{orderId}",
      confirmation:
        "Your custom map product is being prepared. You'll receive a confirmation email with tracking info.",
      totalCharged: "Total charged:",
      delivery: "Delivery:",
      defaultDelivery: "5-10 business days",
      selectProduct: "Select a product",
      configure: "Configure",
      proceedCheckout: "Proceed to Checkout",
      placeDemoOrder: "Place Demo Order",
      placeOrder: "Place Order",
      placingOrder: "Placing order...",
      reviewPayment: "Review payment",
      preparingPayment: "Preparing payment...",
      continueToPayment: "Continue to payment",
      done: "Done",
      acceptTerms: "You must accept the Terms of Sale before proceeding.",
      confirmClose:
        "Are you sure you want to close this order flow? Your order has not been placed.",
      fillRequired: "Please fill in all required shipping fields.",
      orderFailed: "Order failed: {message}",
      printSize: "Print Size",
      printSmall: "Small",
      printMedium: "Medium",
      printLarge: "Large",
      redirectingToPayment: "Redirecting to payment...",
      paymentCancelled: "Payment was cancelled. Your order was not placed.",
      completingOrder: "Completing your order...",
      securedByStripe: "Secure payment via Stripe",
      payViaStripe: "Pay via Stripe",
      paymentStepTitle: "Payment",
      paymentIntro: "Review your order before continuing to Stripe's secure checkout.",
      paymentModeLive: "Live payment mode. Your card will be charged by Stripe.",
      paymentModeTest: "Test payment mode. Use Stripe test card details.",
      paymentModeDemo: "Payment is unavailable while the shop is in demo mode.",
      webhookReady: "Automatic order fulfillment is active.",
      webhookMissing: "Webhook secret is missing; automatic fulfillment may be incomplete.",
    },
    legal: {
      termsTitle: "Terms of Service",
      privacyTitle: "Privacy Policy",
      termsContent: `
        <h3>1. Acceptance of Terms</h3>
        <p>By using Map Poster Generator, you agree to these terms. This is a service for creating custom map artworks based on OpenStreetMap data.</p>
        <h3>2. Custom Products & Returns</h3>
        <p>Our products are <strong>custom-made</strong> specifically for you. According to EU consumer law, the 14-day right of withdrawal does not apply to custom-made goods once production has started. All sales are final.</p>
        <h3>3. Map Data & Licensing</h3>
        <p>Map data is provided by © <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors under the ODbL license. You are allowed to use the generated posters for personal use.</p>
        <h3>4. Print Quality</h3>
        <p>While we use high-resolution rendering, actual print colors may vary slightly from what you see on your screen due to monitor calibration differences.</p>
      `,
      privacyContent: `
        <h3>1. Data Collection</h3>
        <p>We value your privacy. We store your shipping information (name, address, email) in your browser's <strong>Local Storage</strong> for your convenience. This data stays on your device.</p>
        <h3>2. Third-Party Sharing</h3>
        <p>When you place an order, your shipping details and the design asset are shared with <strong>Gelato</strong>, our global fulfillment partner, to produce and ship your order.</p>
        <h3>3. Cookies</h3>
        <p>We use essential local storage tokens to manage your session and save your preferences. We do not use tracking cookies for advertising.</p>
      `,
    },
  },
  da: {
    nav: { generate: "Generer Plakat", map: "Kort", advanced: "Avanceret", gallery: "Galleri" },
    form: {
      title: "Tilpas Din Plakat",
      popularCities: "Populære Byer",
      city: "By",
      cityPlaceholder: "Indtast bynavn",
      country: "Land",
      countryPlaceholder: "Indtast landnavn",
      theme: "Stil Tema",
      zoom: "Zoom/Afstand",
      generate: "Generer Nyt Kort",
      printOptions: "Print Muligheder",
      vertical: "Vertikal",
      square: "Kvadratisk",
      coordinates: "Koordinater",
      editOnMap: "Rediger på kort",
      buyPhysical: "Køb Fysisk",
      stop: "Stop Generering",
    },
    preview: { placeholder: "Indtast en by og generer din plakat" },
    map: {
      title: "Vælg Placering",
      instructions:
        "Klik på kortet for at vælge centrum for din plakat. Brug zoom-kontroller eller scroll for at justere visningen.",
      center: "Centerpunkt",
      radius: "Plakat Radius",
      search: "Søg Placering",
      searchPlaceholder: "Søg efter et sted...",
      apply: "Anvend & Generer",
    },
    download: {
      ready: "Klar til print?",
      resolution: "Ultra Høj Opløsning (300 DPI)",
      png: "PNG",
      svg: "SVG",
      pdf: "PDF",
      share: "Del",
      shareCopied: "Link kopieret til udklipsholder!",
      shareError: "Kunne ikke kopiere link",
    },
    carousel: {
      title: "Vælg Tema",
      loading: "Genererer...",
      tip:
        "Tip: Brug karrusellen nedenfor for hurtigt at skifte mellem forskellige visuelle temaer.",
    },
    gallery: {
      title: "Mit Galleri",
      count: "Du har {count} gemt kortplakat",
      countPlural: "Du har {count} gemte kortplakater",
      empty: "Ingen plakater endnu",
      emptyDesc: "Generer din første plakat for at komme i gang!",
      createFirst: "Opret Første Plakat",
      allPosters: "Alle Plakater",
      newPoster: "Ny Plakat",
      newPosterDesc: "Opret et nyt design",
    },
    modal: {
      galleryView: "Galleri Visning",
      downloadPoster: "Download Plakat",
      artwork: "Kunstværk {current} af {total}",
      previous: "Forrige",
      next: "Næste",
    },
    footer: {
      copyright: "© 2024 Map Poster Generator. Alle rettigheder forbeholdes.",
      terms: "Servicevilkår",
      privacy: "Privatlivspolitik",
      help: "Hjælpecenter",
    },
    privacy: {
      title: "Privatliv og oplevelse",
      text:
        "Vi bruger lokal lagring til at huske dine leveringsoplysninger og indstillinger, så oplevelsen bliver hurtigere. Dine data forlader aldrig din enhed, medmindre du afgiver en ordre.",
      accept: "Forstået",
    },
    advanced: {
      title: "Avancerede Indstillinger",
      customText: "Brugerdefineret Tekst",
      customTextDesc:
        "Tilsidesæt standard by/land tekst på din plakat. Efterlad tom for at bruge registrerede værdier.",
      cityText: "By Tekst",
      cityPlaceholder: "f.eks. KØBENHAVN",
      countryText: "Land Tekst",
      countryPlaceholder: "f.eks. DANMARK",
      coordinates: "Koordinater",
      coordinatesDesc:
        "Tilsidesæt koordinaterne vist på plakaten. Efterlad tom for at bruge registrerede værdier.",
      latitude: "Breddegrad",
      longitude: "Længdegrad",
      typography: "Typografi",
      typographyDesc: "Vælg skrifttype til teksten på din plakat.",
      fontFamily: "Skrifttype",
      layers: "Kortlag",
      layersDesc: "Slå tunge kortlag til/fra for at undgå fejl ved hentning af data.",
      showBuildings: "Vis bygninger",
      showRailways: "Vis jernbaner",
      reset: "Nulstil til Standard",
    },
    loading: {
      finding: "Finder placering...",
      fetching: "Henter kortdata...",
      rendering: "Renderer plakat...",
      saving: "Gemmer i galleri...",
      applying: "Anvender nyt tema...",
      generating: "Genererer...",
    },
    errors: {
      cityCountry: "Indtast venligst både by og land",
      geocode: "Kunne ikke finde placering",
      generate: "Kunne ikke generere plakat",
    },
    pod: {
      title: "Bestil fysisk produkt",
      demoMode: "Demotilstand - ingen rigtige ordrer",
      testMode: "Stripe testtilstand",
      paymentNotConfigured: "Betaling er ikke konfigureret",
      shippingCountry: "Leveringsland",
      comingSoon: "Kommer snart",
      comingSoonDesc: "Fysiske tryk er på vej. Tjek tilbage snart!",
      wallArt: "Vægkunst",
      apparel: "Beklædning",
      accessories: "Tilbehør",
      findingProducts: "Finder tilgængelige produkter for {country}...",
      notAvailableIn: "Ikke tilgængelig i {country}",
      unavailable: "ikke tilgængelig",
      fromPrice: "fra {price}",
      backToProducts: "Tilbage til produkter",
      size: "Størrelse",
      quantity: "Antal",
      price: "Pris",
      backToConfig: "Tilbage til konfiguration",
      backToShipping: "Tilbage til levering",
      shippingAddress: "Leveringsadresse",
      firstName: "Fornavn",
      lastName: "Efternavn",
      email: "E-mail",
      address1: "Adresse linje 1",
      address2: "Adresse linje 2 (valgfri)",
      city: "By",
      postcode: "Postnummer",
      state: "Region / provins",
      termsTitle: "Salgsbetingelser og privatliv",
      termsText:
        "Jeg forstår, at dette er et specialfremstillet produkt. Ved at afgive ordren fraskriver jeg mig udtrykkeligt min 14 dages fortrydelsesret. Jeg accepterer, at min adresse og mit design deles med Gelato med henblik på produktion og levering. Farver kan afvige en smule fra skærmvisningen.",
      orderSummary: "Ordreoversigt",
      shippingEstimate: "Levering beregnes af leverandøren. Forventet levering: 5-10 hverdage.",
      orderPlaced: "Ordren er afgivet!",
      orderNumber: "Ordre #{orderId}",
      confirmation:
        "Dit personlige kortprodukt bliver klargjort. Du modtager en bekræftelsesmail med trackingoplysninger.",
      totalCharged: "Opkrævet i alt:",
      delivery: "Levering:",
      defaultDelivery: "5-10 hverdage",
      selectProduct: "Vælg et produkt",
      configure: "Konfigurer",
      proceedCheckout: "Gå til betaling",
      placeDemoOrder: "Afgiv demoordre",
      placeOrder: "Afgiv ordre",
      placingOrder: "Afgiver ordre...",
      reviewPayment: "Gennemgå betaling",
      preparingPayment: "Forbereder betaling...",
      continueToPayment: "Fortsæt til betaling",
      done: "Færdig",
      acceptTerms: "Du skal acceptere salgsbetingelserne, før du fortsætter.",
      confirmClose: "Er du sikker på, at du vil lukke bestillingen? Din ordre er ikke afgivet.",
      fillRequired: "Udfyld venligst alle påkrævede leveringsfelter.",
      orderFailed: "Ordre mislykkedes: {message}",
      printSize: "Printstørrelse",
      printSmall: "Lille",
      printMedium: "Medium",
      printLarge: "Stor",
      redirectingToPayment: "Sender til betaling...",
      paymentCancelled: "Betalingen blev annulleret. Din ordre blev ikke afgivet.",
      completingOrder: "Færdiggør din ordre...",
      securedByStripe: "Sikker betaling via Stripe",
      payViaStripe: "Betal via Stripe",
      paymentStepTitle: "Betaling",
      paymentIntro: "Gennemgå din ordre, før du fortsætter til Stripes sikre checkout.",
      paymentModeLive: "Live betalingstilstand. Dit kort bliver opkrævet af Stripe.",
      paymentModeTest: "Testbetalingstilstand. Brug Stripes testkortoplysninger.",
      paymentModeDemo: "Betaling er ikke tilgængelig, mens shoppen er i demotilstand.",
      webhookReady: "Automatisk ordrebehandling er aktiv.",
      webhookMissing: "Webhook secret mangler; automatisk ordrebehandling kan være ufuldstændig.",
    },
    legal: {
      termsTitle: "Servicevilkår",
      privacyTitle: "Privatlivspolitik",
      termsContent: `
        <h3>1. Accept af vilkår</h3>
        <p>Ved at bruge Map Poster Generator accepterer du disse vilkår. Dette er en tjeneste til at skabe brugerdefinerede kort-kunstværker baseret på OpenStreetMap-data.</p>
        <h3>2. Specialfremstillede produkter & Retur</h3>
        <p>Vores produkter er <strong>specialfremstillede</strong> specifikt til dig. I henhold til Forbrugeraftaleloven bortfalder de 14 dages fortrydelsesret for specialfremstillede varer, så snart produktionen er påbegyndt.</p>
        <h3>3. Kortdata & Licensering</h3>
        <p>Kortdata leveres af © <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> bidragydere under ODbL-licensen. Du har ret til at bruge de genererede plakater til personlig brug.</p>
        <h3>4. Printkvalitet</h3>
        <p>Selvom vi bruger højopløsnings-rendering, kan de faktiske printfarver variere en smule fra det, du ser på din skærm, pga. forskelle i skærmkalibrering.</p>
      `,
      privacyContent: `
        <h3>1. Dataindsamling</h3>
        <p>Vi værdsætter dit privatliv. Vi gemmer dine leveringsoplysninger (navn, adresse, e-mail) i din browsers <strong>Local Storage</strong> for din bekvemmelighed. Disse data forbliver på din enhed.</p>
        <h3>2. Deling med tredjepart</h3>
        <p>Når du afgiver en ordre, deles dine leveringsoplysninger og designfilen med <strong>Gelato</strong>, vores globale produktionspartner, for at producere og sende din ordre.</p>
        <h3>3. Cookies</h3>
        <p>Vi bruger nødvendige "local storage tokens" til at håndtere din session og gemme dine præferencer. Vi bruger ikke tracking-cookies til annoncering.</p>
      `,
    },
  },
  es: {
    nav: { generate: "Generar Póster", map: "Mapa", advanced: "Avanzado", gallery: "Galería" },
    form: {
      title: "Personaliza Tu Póster",
      popularCities: "Ciudades Populares",
      city: "Ciudad",
      cityPlaceholder: "Ingresa nombre de ciudad",
      country: "País",
      countryPlaceholder: "Ingresa nombre de país",
      theme: "Tema de Estilo",
      zoom: "Zoom/Distancia",
      generate: "Generar Nueva Vista",
      printOptions: "Opciones de Impresión",
      vertical: "Vertical",
      square: "Cuadrado",
      coordinates: "Coordenadas",
      editOnMap: "Editar en mapa",
      buyPhysical: "Comprar Físico",
      stop: "Detener Generación",
    },
    preview: { placeholder: "Ingresa una ciudad y genera tu póster" },
    map: {
      title: "Seleccionar Ubicación",
      instructions:
        "Haz clic en el mapa para establecer el punto central de tu póster. Usa los controles de zoom o desplázate para ajustar la vista.",
      center: "Punto Central",
      radius: "Radio del Póster",
      search: "Buscar Ubicación",
      searchPlaceholder: "Buscar un lugar...",
      apply: "Aplicar y Generar",
    },
    download: {
      ready: "¿Listo para imprimir?",
      resolution: "Ultra Alta Resolución (300 DPI)",
      png: "PNG",
      svg: "SVG",
      pdf: "PDF",
      share: "Compartir",
      shareCopied: "¡Enlace copiado al portapapeles!",
      shareError: "Error al copiar enlace",
    },
    carousel: {
      title: "Elegir Tema",
      loading: "Generando...",
      tip:
        "Consejo: Usa el carrusel a continuación para cambiar rápidamente entre diferentes temas visuales.",
    },
    gallery: {
      title: "Mi Galería",
      count: "Tienes {count} póster de mapa guardado",
      countPlural: "Tienes {count} pósters de mapas guardados",
      empty: "Sin pósters aún",
      emptyDesc: "¡Genera tu primer póster para comenzar!",
      createFirst: "Crear Primer Póster",
      allPosters: "Todos los Pósters",
      newPoster: "Nuevo Póster",
      newPosterDesc: "Crear un nuevo diseño",
    },
    modal: {
      galleryView: "Vista de Galería",
      downloadPoster: "Descargar Póster",
      artwork: "Arte {current} de {total}",
      previous: "Anterior",
      next: "Siguiente",
    },
    footer: {
      copyright: "© 2024 Map Poster Generator. Todos los derechos reservados.",
      terms: "Términos de Servicio",
      privacy: "Política de Privacidad",
      help: "Centro de Ayuda",
    },
    privacy: {
      title: "Privacidad y experiencia",
      text:
        "Usamos almacenamiento local para recordar sus datos de envío y configuración para una experiencia más rápida. Sus datos nunca salen de su dispositivo a menos que realice un pedido.",
      accept: "Entendido",
    },
    advanced: {
      title: "Configuración Avanzada",
      customText: "Texto Personalizado",
      customTextDesc:
        "Anula el texto predeterminado de ciudad/país en tu póster. Deja vacío para usar valores detectados.",
      cityText: "Texto de Ciudad",
      cityPlaceholder: "ej. MADRID",
      countryText: "Texto de País",
      countryPlaceholder: "ej. ESPAÑA",
      coordinates: "Coordenadas",
      coordinatesDesc:
        "Anula las coordenadas mostradas en el póster. Deja vacío para usar valores detectados.",
      latitude: "Latitud",
      longitude: "Longitud",
      typography: "Tipografía",
      typographyDesc: "Elige el estilo de fuente para el texto de tu póster.",
      fontFamily: "Familia de Fuentes",
      layers: "Capas del Mapa",
      layersDesc: "Activa o desactiva capas pesadas para evitar errores de carga.",
      showBuildings: "Mostrar Edificios",
      showRailways: "Mostrar Ferrocarriles",
      reset: "Restablecer Valores",
    },
    loading: {
      finding: "Buscando ubicación...",
      fetching: "Obteniendo datos del mapa...",
      rendering: "Renderizando póster...",
      saving: "Guardando en galería...",
      applying: "Aplicando nuevo tema...",
      generating: "Generando...",
    },
    errors: {
      cityCountry: "Por favor ingresa ciudad y país",
      geocode: "No se pudo geocodificar la ubicación",
      generate: "No se pudo generar el póster",
    },
    pod: {
      title: "Pedir producto físico",
      demoMode: "Modo demo - sin pedidos reales",
      testMode: "Modo de prueba de Stripe",
      paymentNotConfigured: "Pago no configurado",
      shippingCountry: "País de envío",
      comingSoon: "Próximamente",
      comingSoonDesc: "Las impresiones físicas están en camino. ¡Vuelve pronto!",
      wallArt: "Arte de pared",
      apparel: "Ropa",
      accessories: "Accesorios",
      findingProducts: "Buscando productos disponibles para {country}...",
      notAvailableIn: "No disponible en {country}",
      unavailable: "no disponible",
      fromPrice: "desde {price}",
      backToProducts: "Volver a productos",
      size: "Talla",
      quantity: "Cantidad",
      price: "Precio",
      backToConfig: "Volver a configuración",
      backToShipping: "Volver al envío",
      shippingAddress: "Dirección de envío",
      firstName: "Nombre",
      lastName: "Apellido",
      email: "Correo electrónico",
      address1: "Dirección Línea 1",
      address2: "Dirección Línea 2 (opcional)",
      city: "Ciudad",
      postcode: "Código postal",
      state: "Estado / Provincia",
      termsTitle: "Términos de venta y privacidad",
      termsText:
        "Entiendo que este es un producto hecho a medida. Al realizar este pedido, renuncio explícitamente a mi derecho de desistimiento de 14 días. Acepto que mi dirección y diseño se compartirán con Gelato para el cumplimiento. Los colores pueden variar ligeramente de la vista previa en pantalla.",
      orderSummary: "Resumen del pedido",
      shippingEstimate: "Envío calculado por el proveedor. Entrega estimada: 5-10 días hábiles.",
      orderPlaced: "¡Pedido realizado!",
      orderNumber: "Pedido #{orderId}",
      confirmation:
        "Su producto de mapa personalizado está siendo preparado. Recibirá un correo electrónico de confirmación con información de seguimiento.",
      totalCharged: "Total cobrado:",
      delivery: "Entrega:",
      defaultDelivery: "5-10 días hábiles",
      selectProduct: "Seleccionar un producto",
      configure: "Configurar",
      proceedCheckout: "Proceder al pago",
      placeDemoOrder: "Realizar pedido de demostración",
      placeOrder: "Realizar pedido",
      placingOrder: "Realizando pedido...",
      reviewPayment: "Revisar pago",
      preparingPayment: "Preparando pago...",
      continueToPayment: "Continuar al pago",
      done: "Hecho",
      acceptTerms: "Debe aceptar los Términos de venta antes de continuar.",
      confirmClose:
        "¿Seguro que desea cerrar este flujo de pedido? Su pedido aún no se ha realizado.",
      fillRequired: "Por favor, complete todos los campos de envío obligatorios.",
      orderFailed: "Error en el pedido: {message}",
      printSize: "Tamaño de impresión",
      printSmall: "Pequeño",
      printMedium: "Mediano",
      printLarge: "Grande",
      redirectingToPayment: "Redirigiendo al pago...",
      paymentCancelled: "El pago fue cancelado. Su pedido no fue realizado.",
      completingOrder: "Completando su pedido...",
      securedByStripe: "Pago seguro via Stripe",
      payViaStripe: "Pagar via Stripe",
      paymentStepTitle: "Pago",
      paymentIntro: "Revise su pedido antes de continuar al checkout seguro de Stripe.",
      paymentModeLive: "Modo de pago real. Stripe cobrará su tarjeta.",
      paymentModeTest: "Modo de prueba. Use datos de tarjeta de prueba de Stripe.",
      paymentModeDemo: "El pago no está disponible mientras la tienda está en modo demo.",
      webhookReady: "El cumplimiento automático de pedidos está activo.",
      webhookMissing:
        "Falta el secreto del webhook; el cumplimiento automático puede estar incompleto.",
    },
    legal: {
      termsTitle: "Términos de Servicio",
      privacyTitle: "Política de Privacidad",
      termsContent: `
        <h3>1. Aceptación de los Términos</h3>
        <p>Al utilizar Map Poster Generator, usted acepta estos términos. Este es un servicio para crear obras de arte de mapas personalizadas basadas en datos de OpenStreetMap.</p>
        <h3>2. Productos personalizados y devoluciones</h3>
        <p>Nuestros productos están <strong>hechos a medida</strong> específicamente para usted. De acuerdo con la ley del consumidor de la UE, el derecho de desistimiento de 14 días no se aplica a los bienes hechos a medida una vez que ha comenzado la producción. Todas las ventas son finales.</p>
        <h3>3. Datos del mapa y licencias</h3>
        <p>Los datos del mapa son proporcionados por los colaboradores de © <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> bajo la licencia ODbL. Se le permite utilizar los pósteres generados para uso personal.</p>
        <h3>4. Calidad de impresión</h3>
        <p>Si bien utilizamos renderizado de alta resolución, los colores de impresión reales pueden variar ligeramente de lo que ve en su pantalla debido a las diferencias de calibración del monitor.</p>
      `,
      privacyContent: `
        <h3>1. Recopilación de datos</h3>
        <p>Valoramos su privacidad. Almacenamos su información de envío (nombre, dirección, correo electrónico) en el <strong>Almacenamiento Local</strong> de su navegador para su conveniencia. Estos datos permanecen en su dispositivo.</p>
        <h3>2. Intercambio con terceros</h3>
        <p>Cuando realiza un pedido, sus datos de envío y el recurso de diseño se comparten con <strong>Gelato</strong>, nuestro socio de cumplimiento global, para producir y enviar su pedido.</p>
        <h3>3. Cookies</h3>
        <p>Utilizamos tokens de almacenamiento local esenciales para administrar su sesión y guardar sus preferencias. No utilizamos cookies de seguimiento para publicidad.</p>
      `,
    },
  },
  zh: {
    nav: { generate: "生成海报", map: "地图", advanced: "高级", gallery: "画廊" },
    form: {
      title: "定制您的海报",
      popularCities: "热门城市",
      city: "城市",
      cityPlaceholder: "输入城市名称",
      country: "国家",
      countryPlaceholder: "输入国家名称",
      theme: "风格主题",
      zoom: "缩放/距离",
      generate: "生成新视图",
      printOptions: "打印选项",
      vertical: "竖版",
      square: "方形",
      coordinates: "坐标",
      editOnMap: "在地图上编辑",
      buyPhysical: "购买实物",
      stop: "停止生成",
    },
    preview: { placeholder: "输入城市并生成您的海报" },
    map: {
      title: "选择位置",
      instructions: "点击地图设置海报的中心点。使用缩放控件或滚动调整视图。",
      center: "中心点",
      radius: "海报半径",
      search: "搜索位置",
      searchPlaceholder: "搜索地点...",
      apply: "应用并生成",
    },
    download: {
      ready: "准备打印？",
      resolution: "超高分辨率 (300 DPI)",
      png: "PNG",
      svg: "SVG",
      pdf: "PDF",
      share: "分享",
      shareCopied: "链接已复制到剪贴板！",
      shareError: "复制链接失败",
    },
    carousel: {
      title: "选择主题",
      loading: "生成中...",
      tip: "提示：使用下方的轮播快速切换不同的视觉主题。",
    },
    gallery: {
      title: "我的画廊",
      count: "您有 {count} 张已保存的地图海报",
      countPlural: "您有 {count} 张已保存的地图海报",
      empty: "暂无海报",
      emptyDesc: "生成您的第一张海报开始吧！",
      createFirst: "创建第一张海报",
      allPosters: "所有海报",
      newPoster: "新海报",
      newPosterDesc: "创建新设计",
    },
    modal: {
      galleryView: "画廊视图",
      downloadPoster: "下载海报",
      artwork: "作品 {current} / {total}",
      previous: "上一张",
      next: "下一张",
    },
    footer: {
      copyright: "© 2024 地图海报生成器。保留所有权利。",
      terms: "服务条款",
      privacy: "隐私政策",
      help: "帮助中心",
    },
    privacy: {
      title: "隐私与体验",
      text:
        "我们使用本地存储来记住您的配送信息和设置，以提供更快的体验。除非您下订单，否则您的数据不会离开您的设备。",
      accept: "知道了",
    },
    advanced: {
      title: "高级设置",
      customText: "自定义文字",
      customTextDesc: "覆盖海报上的默认城市/国家文字。留空则使用检测到的值。",
      cityText: "城市文字",
      cityPlaceholder: "例如：北京",
      countryText: "国家文字",
      countryPlaceholder: "例如：中国",
      coordinates: "坐标",
      coordinatesDesc: "覆盖海报上显示的坐标。留空则使用检测到的值。",
      latitude: "纬度",
      longitude: "经度",
      typography: "字体排版",
      typographyDesc: "选择海报文字的字体样式。",
      fontFamily: "字体系列",
      layers: "地图图层",
      layersDesc: "开启或关闭较重的图层以防止数据获取错误。",
      showBuildings: "显示建筑物",
      showRailways: "显示铁路",
      reset: "重置为默认",
    },
    loading: {
      finding: "正在查找位置...",
      fetching: "正在获取地图数据...",
      rendering: "正在渲染海报...",
      saving: "正在保存到画廊...",
      applying: "正在应用新主题...",
      generating: "生成中...",
    },
    errors: {
      cityCountry: "请输入城市和国家",
      geocode: "无法找到位置",
      generate: "无法生成海报",
    },
    pod: {
      title: "订购实物产品",
      demoMode: "演示模式 - 无真实订单",
      testMode: "Stripe 测试模式",
      paymentNotConfigured: "付款未配置",
      shippingCountry: "运送国家",
      comingSoon: "即将推出",
      comingSoonDesc: "实体印刷品即将上线，敬请期待！",
      wallArt: "墙面艺术",
      apparel: "服装",
      accessories: "配饰",
      findingProducts: "正在查找 {country} 的可用产品...",
      notAvailableIn: "在 {country} 不可用",
      unavailable: "不可用",
      fromPrice: "{price} 起",
      backToProducts: "返回产品列表",
      size: "尺寸",
      quantity: "数量",
      price: "价格",
      backToConfig: "返回配置",
      backToShipping: "返回配送信息",
      shippingAddress: "收货地址",
      firstName: "名",
      lastName: "姓",
      email: "电子邮件",
      address1: "地址行 1",
      address2: "地址行 2（可选）",
      city: "城市",
      postcode: "邮政编码",
      state: "州 / 省",
      termsTitle: "销售条款与隐私",
      termsText:
        "我了解这是定制产品。通过下订单，我明确放弃 14 天的撤回权。我同意将我的地址和设计与 Gelato 共享以进行履行。打印颜色可能与屏幕预览略 contributors 有不同。",
      orderSummary: "订单摘要",
      shippingEstimate: "运费由供应商计算。预计送达：5-10 个工作日。",
      orderPlaced: "订单已下达！",
      orderNumber: "订单号 #{orderId}",
      confirmation: "您的定制地图产品正在准备中。您将收到一封包含跟踪信息的确认电子邮件。",
      totalCharged: "总费用：",
      delivery: "送达：",
      defaultDelivery: "5-10 个工作日",
      selectProduct: "选择产品",
      configure: "配置",
      proceedCheckout: "继续结账",
      placeDemoOrder: "下达演示订单",
      placeOrder: "下订单",
      placingOrder: "正在下订单...",
      reviewPayment: "查看付款",
      preparingPayment: "正在准备付款...",
      continueToPayment: "继续付款",
      done: "完成",
      acceptTerms: "您必须在继续之前接受销售条款。",
      confirmClose: "确定要关闭此订购流程吗？您的订单尚未提交。",
      fillRequired: "请填写所有必填的运送字段。",
      orderFailed: "订单失败：{message}",
      printSize: "打印尺寸",
      printSmall: "小",
      printMedium: "中",
      printLarge: "大",
      redirectingToPayment: "正在跳转到付款...",
      paymentCancelled: "付款已取消。您的订单未被下达。",
      completingOrder: "正在完成您的订单...",
      securedByStripe: "通过 Stripe 安全付款",
      payViaStripe: "通过 Stripe 付款",
      paymentStepTitle: "付款",
      paymentIntro: "请在继续前往 Stripe 安全结账前查看您的订单。",
      paymentModeLive: "实时付款模式。Stripe 将向您的银行卡收费。",
      paymentModeTest: "测试付款模式。请使用 Stripe 测试卡信息。",
      paymentModeDemo: "商店处于演示模式时无法付款。",
      webhookReady: "自动订单履行已启用。",
      webhookMissing: "缺少 webhook secret；自动订单履行可能不完整。",
    },
    legal: {
      termsTitle: "服务条款",
      privacyTitle: "隐私政策",
      termsContent: `
        <h3>1. 接受条款</h3>
        <p>通过使用 Map Poster Generator，您同意这些条款。这是一个基于 OpenStreetMap 数据创建自定义地图艺术作品的服务。</p>
        <h3>2. 定制产品与退货</h3>
        <p>我们的产品是专为您<strong>定制</strong>的。根据欧盟消费者法，14 天的撤回权不适用于一旦开始生产的定制商品。所有销售均为最终销售。</p>
        <h3>3. 地图数据与许可</h3>
        <p>地图数据由 © <a href="https://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> 贡献者根据 ODbL 许可提供。您可以将生成的海报用于个人用途。</p>
        <h3>4. 打印质量</h3>
        <p>虽然我们使用高分辨率渲染，但由于显示器校准差异，实际打印颜色可能与您在屏幕上看到的略有不同。</p>
      `,
      privacyContent: `
        <h3>1. 数据收集</h3>
        <p>我们重视您的隐私。为了您的方便，我们将您的运送信息（姓名、地址、电子邮件）存储在浏览器的<strong>本地存储</strong>中。这些数据保留在您的设备上。</p>
        <h3>2. 第三方共享</h3>
        <p>当您下订单时，您的运送详情和设计资产将与我们的全球履行合作伙伴 <strong>Gelato</strong> 共享，以生产和运送您的订单。</p>
        <h3>3. Cookie</h3>
        <p>我们使用必要的本地存储令牌来管理您的会话并保存您的首选项。我们不使用跟踪 Cookie 进行广告。</p>
      `,
    },
  },
};

export const langNames: Record<string, string> = {
  en: "English",
  da: "Dansk",
  es: "Español",
  zh: "中文",
};

export function getTranslation(key: string, translations: any): any {
  const keys = key.split(".");
  let value = translations;
  for (const k of keys) {
    value = value?.[k];
  }
  return value;
}

export function t(
  key: string,
  currentLang: string,
  replacements: Record<string, any> = {},
): string {
  const translations = i18n[currentLang] || i18n.en;
  let value = getTranslation(key, translations);
  if (value === undefined && currentLang !== "en") {
    value = getTranslation(key, i18n.en);
  }
  if (value === undefined) value = key;
  for (const [k, v] of Object.entries(replacements)) {
    if (typeof value === "string") {
      value = value.replace(`{${k}}`, String(v));
    }
  }
  return value as string;
}
